import { useMemo, useState } from 'react';
import { Button, Select, Spin } from 'antd';
import { BookCopy } from 'lucide-react';
import { Outlet, createPath, useLocation, useMatch, useNavigate, useParams, useSearchParams } from 'react-router';
import { ARRANGEMENT_TYPES } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { TwoColumnLayout } from '@/components/TwoColumnLayout';
import { useAuth } from '@/hooks/useAuth';
import { can } from '@/lib/accessRole';
import { templateStateOf } from '@/lib/statusPresentation';
import { LIBRARY_COPY, TEMPLATES_COPY } from '../copy';
import { typesForEvent, useTemplateDecisions, useTemplateList, useTemplateSet } from '../templateQueries';
import { OpenDecisionsPanel } from './OpenDecisionsPanel';
import { LIBRARY_PICK } from './paneFocus';
import { SetupGuide } from './SetupGuide';
import { TemplateTree, type TreeCurrent } from './TemplateTree';
import {
  NO_FILTERS,
  buildTree,
  rareColumnsOf,
  stateFilterOf,
  unusualTemplatesOf,
  type ColumnFilter,
  type StateFilter,
  type TreeFilters,
} from './templateTree';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2 · §3] THE TEMPLATE LIBRARY — `/templates/:setKey`, the review site's library: a tree of
 *  every template beside the pane it opens, under a panel saying what holds templates back.
 *  [amended P7C.1 §2] That panel is a link stating the outstanding count, opening the same content in a
 *  dialog; the dialog closes when the pathname moves (`closeOn`), as the tree's own bar does.
 *
 *  ⚠️ A LAYOUT ROUTE. The tree, its filters, the open Parts and the selection live HERE, so they
 *  survive moving between panes; each pane (`t/:templateId`, `section/:sectionKey`, `part/:partId`) is
 *  a child route that reads the same cached set and list through the same hooks — one request each,
 *  shared — and the index opens the first template, as the review site does.
 *
 *  ⚠️ ONLY A READY TEMPLATE IS SELECTABLE, BY THE SERVER'S READINESS — a created one that is ready keeps
 *  its box, and planning it again goes through Create's acknowledgement. "Select all ready" picks the
 *  ready ones that are not already created.
 *
 *  ⚠️ THE TWO-COLUMN SPLIT IS `TwoColumnLayout`'s `tree` variant — the one collapse mechanism: below
 *  its split the tree is a bar, "Browse all N templates", that opens in place and closes after a pick.
 *
 *  [amended P7C §3] "Setup guide" is the FIRST header action — the steps in order, with this set's own
 *  figures; it reads nothing until it is opened, so this layout's request census is unchanged.
 *
 *  [amended TEMPLATE-FIND] Enter in the tree's filter opens the one template it leaves, through the same
 *  navigation a pick makes — so it reads nothing a pick would not, and REPLACES the history entry where a
 *  click on that row would (the template already open), so Back is never one step behind.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const TemplateLibraryLayout = (): React.ReactElement => {
  const { setKey = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const set = useTemplateSet(setKey);
  const list = useTemplateList(setKey);
  const sources = useTemplateDecisions(setKey, 'SOURCE');
  const { officer } = useAuth();
  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<TreeFilters>(NO_FILTERS);
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const templateMatch = useMatch('/templates/:setKey/t/:templateId');
  const sectionMatch = useMatch('/templates/:setKey/section/:sectionKey');
  const partMatch = useMatch('/templates/:setKey/part/:partId');

  const types = typesForEvent(set.data?.event);
  const yearParam = Number(searchParams.get('year'));
  const years: number[] = types.map((type) => ARRANGEMENT_TYPES[type].year);
  const year = years.includes(yearParam) ? yearParam : (years[0] ?? null);
  const type = types.find((key) => ARRANGEMENT_TYPES[key].year === year);

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const sections = useMemo(() => set.data?.sections ?? [], [set.data]);
  const rare = useMemo(() => rareColumnsOf(items), [items]);
  const unusual = useMemo(() => unusualTemplatesOf(items, rare), [items, rare]);
  const unusualIds = useMemo(() => new Set(unusual.keys()), [unusual]);
  const tree = useMemo(() => buildTree(sections, items, filters, unusualIds), [sections, items, filters, unusualIds]);

  if (set.isPending || list.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  if (!set.data) return <EmptyState title={TEMPLATES_COPY.noSets} />;

  const byId = new Map(items.map((item) => [item.id, item] as const));
  const sectionPart = (key: string): string | null => sections.find((section) => section.key === key)?.part ?? null;
  const current: TreeCurrent = templateMatch?.params.templateId
    ? {
        kind: 'template',
        id: templateMatch.params.templateId,
        part: byId.get(templateMatch.params.templateId)?.part ?? null,
      }
    : sectionMatch?.params.sectionKey
      ? { kind: 'section', key: sectionMatch.params.sectionKey, part: sectionPart(sectionMatch.params.sectionKey) }
      : partMatch?.params.partId
        ? { kind: 'part', part: partMatch.params.partId }
        : null;

  const stateCounts: Record<StateFilter, number> = {
    ALL: items.length,
    BLOCKED: 0,
    READY: 0,
    CREATED: 0,
    NOT_BUILT: 0,
  };
  for (const item of items) stateCounts[stateFilterOf(templateStateOf(item))] += 1;
  const columnCounts: Record<ColumnFilter, number> = {
    ALL: items.length,
    USUAL: items.length - unusualIds.size,
    UNUSUAL: unusualIds.size,
  };
  /* Ready by the SERVER, and not already created — what "select all ready" means to a person. */
  const readyIds = items
    .filter((item) => item.readiness.state === 'READY' && templateStateOf(item) === 'READY')
    .map((item) => item.id);
  const planHref = year === null ? null : `/templates/${setKey}/create?year=${String(year)}&t=${selected.join(',')}`;
  const yearSearch = searchParams.has('year') ? `?year=${searchParams.get('year') ?? ''}` : '';

  return (
    <div className="flex flex-col gap-4" data-testid="template-library">
      <PageHeader
        icon={BookCopy}
        title={LIBRARY_COPY.title}
        subtitle={`${set.data.title} — ${LIBRARY_COPY.lede}`}
        actions={
          <>
            <SetupGuide setKey={setKey} set={set.data} items={items} rareColumns={rare} year={year} type={type} />
            {year === null ? null : (
              <Button
                onClick={() => void navigate(`/templates/${setKey}/calendar/${String(year)}`)}
                data-testid="library-dates"
              >
                {LIBRARY_COPY.festivalDates}
              </Button>
            )}
            <Button onClick={() => void navigate(`/templates/${setKey}/decisions`)} data-testid="library-decisions">
              {LIBRARY_COPY.decisions}
            </Button>
            {type === undefined ? null : (
              <Button
                onClick={() => void navigate(`/templates/${setKey}/publish?type=${type}`)}
                data-testid="library-publish"
              >
                {LIBRARY_COPY.publish}
              </Button>
            )}
            <Button
              type="primary"
              disabled={selected.length === 0 || planHref === null}
              data-testid="library-plan"
              onClick={() => {
                if (planHref !== null) void navigate(planHref);
              }}
            >
              {LIBRARY_COPY.plan}
            </Button>
          </>
        }
        filters={
          years.length > 1 ? (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{LIBRARY_COPY.year}</span>
              <Select
                value={year ?? undefined}
                className="w-28"
                onChange={(next: number) => {
                  setSearchParams((current_) => {
                    current_.set('year', String(next));
                    return current_;
                  });
                }}
                options={years.map((each) => ({ value: each, label: String(each) }))}
              />
            </label>
          ) : undefined
        }
      />

      {type === undefined ? (
        <p className="text-sm text-muted-foreground" data-testid="library-no-type">
          {TEMPLATES_COPY.noTypeForEvent}
        </p>
      ) : null}

      <OpenDecisionsPanel
        setKey={setKey}
        items={items}
        sections={sections}
        outstanding={set.data.decisions.outstanding}
        rareColumns={rare}
        unusual={unusual}
        sources={sources.data?.items}
        mayCreatePostType={officer ? can(officer.accessRole, 'postType.write') : false}
        days={set.data.days}
        closeOn={location.pathname}
      />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span data-testid="library-selected">{LIBRARY_COPY.selected(selected.length)}</span>
        <Button
          size="small"
          data-testid="select-all-ready"
          disabled={readyIds.length === 0}
          onClick={() => {
            setSelected(readyIds);
          }}
        >
          {LIBRARY_COPY.selectAllReady}
        </Button>
        <Button
          size="small"
          type="text"
          disabled={selected.length === 0}
          onClick={() => {
            setSelected([]);
          }}
        >
          {LIBRARY_COPY.clearSelection}
        </Button>
      </div>

      {items.length === 0 ? (
        <EmptyState title={LIBRARY_COPY.noTemplates} />
      ) : (
        <TwoColumnLayout
          variant="tree"
          secondaryLabel={LIBRARY_COPY.browse(items.length)}
          secondaryCloseOn={location.pathname}
          left={
            <TemplateTree
              setKey={setKey}
              tree={tree}
              filters={filters}
              onFilters={setFilters}
              stateCounts={stateCounts}
              columnCounts={columnCounts}
              current={current}
              toggled={toggled}
              onToggle={(part, open) => {
                setToggled((previous) => ({ ...previous, [part]: open }));
              }}
              selected={new Set(selected)}
              onSelect={(id, checked) => {
                setSelected((previous) =>
                  checked ? [...previous.filter((each) => each !== id), id] : previous.filter((each) => each !== id),
                );
              }}
              onOpen={(item) => {
                /*
                 * [TEMPLATE-FIND] Exactly a pick: `LIBRARY_PICK` moves focus to the pane's heading, and the
                 * pathname moving closes the tree's bar (`secondaryCloseOn`).
                 * ⚠️ AND IT REPLACES WHERE A CLICK ON THE ROW WOULD: a `<Link>` to the location already shown
                 * replaces it (react-router's `useLinkClickHandler`, the same `createPath` comparison), so
                 * Enter on the open template pushes no duplicate entry for Back to step through in silence.
                 * It then behaves as that click does: focus moves to the heading unless the template was
                 * itself opened by a pick — the heading's ref is then the same function, and is not called again.
                 */
                const target = { pathname: `/templates/${setKey}/t/${item.id}`, search: yearSearch };
                void navigate(target, {
                  state: LIBRARY_PICK,
                  replace: createPath(location) === createPath(target),
                });
              }}
              search={yearSearch}
            />
          }
          right={
            <section aria-live="polite" className="min-w-0" data-testid="library-pane">
              <Outlet />
            </section>
          }
        />
      )}
    </div>
  );
};

export default TemplateLibraryLayout;
